import { Search } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { AnimatedBrand } from '@/components/atoms/AnimatedBrand';
import { useCommandPalette } from '@/components/CommandPalette';
import { ModuleSelect } from '@/components/molecules/ModuleSelect';
import { NAV_SECTIONS } from '@/config/navigation';
import { ROUTES } from '@/config/routes';
import { useModuleFilter } from '@/context/ModuleFilter';
import { usePermissions } from '@/context/Permissions';
import { useModules } from '@/services/Modules';
import { aggregatePlatformHealth } from '@/utils/moduleHealth';

import type { SidebarHealth } from './MainSidebar.types';

const initialsFromEmail = (email: string): string => {
	const local = email.split('@')[0] ?? '';
	const parts = local.split(/[._-]+/).filter(Boolean);

	if (parts.length === 0) return '?';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const displayNameFromEmail = (email: string): string => {
	const local = email.split('@')[0] ?? '';

	return local
		.split(/[._-]+/)
		.filter(Boolean)
		.map((p) => p[0].toUpperCase() + p.slice(1))
		.join(' ');
};

const NAV_ITEM_BASE = 'flex items-center gap-xs px-xs py-3xs rounded-md text-[13px] leading-5 transition-colors';
const NAV_ITEM_IDLE = 'text-subtle hover:bg-white hover:text-default';
const NAV_ITEM_ACTIVE = 'bg-purple-50 text-purple-700 font-medium hover:bg-purple-50';

const HEALTH_THEME: Record<SidebarHealth['status'], { dot: string; ping: string; container: string; text: string }> = {
	up: {
		dot: 'bg-green-500',
		ping: 'bg-green-500',
		container: 'border-green-100 bg-green-50/60',
		text: 'text-green-800',
	},
	degraded: {
		dot: 'bg-amber-500',
		ping: 'bg-amber-500',
		container: 'border-amber-100 bg-amber-50/60',
		text: 'text-amber-800',
	},
	down: {
		dot: 'bg-red-500',
		ping: 'bg-red-500',
		container: 'border-red-100 bg-red-50/60',
		text: 'text-red-800',
	},
	disabled: {
		dot: 'bg-grey-400',
		ping: 'bg-grey-400',
		container: 'border-subtle bg-grey-50',
		text: 'text-subtle',
	},
	loading: {
		dot: 'bg-grey-400',
		ping: 'bg-grey-400',
		container: 'border-subtle bg-grey-50',
		text: 'text-subtle',
	},
	unknown: {
		dot: 'bg-grey-400',
		ping: 'bg-grey-400',
		container: 'border-subtle bg-grey-50',
		text: 'text-subtle',
	},
};

const STATUS_LABEL_KEY: Record<SidebarHealth['status'], string> = {
	up: 'STATUS_OPERATING',
	degraded: 'STATUS_DEGRADED',
	down: 'STATUS_DOWN',
	disabled: 'STATUS_UNKNOWN',
	loading: 'STATUS_LOADING',
	unknown: 'STATUS_UNKNOWN',
};

export const MainSidebar = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'sidebar' });
	const { open: openPalette } = useCommandPalette();
	const { me, permissions } = usePermissions();
	const { selectedModule, setSelectedModule } = useModuleFilter();
	const modulesQuery = useModules();
	const moduleNames = (modulesQuery.data ?? []).map((m) => m.name);

	// A stale stored selection (e.g. a module that was removed) falls back
	// to the first registered module once the list arrives.
	useEffect(() => {
		const names = (modulesQuery.data ?? []).map((m) => m.name);

		if (names.length > 0 && !names.includes(selectedModule)) {
			setSelectedModule(names[0]);
		}
	}, [modulesQuery.data, selectedModule, setSelectedModule]);

	// An entry without `permission` is visible to everyone who got in.
	const sections = NAV_SECTIONS.map((section) => ({
		...section,
		items: section.items.filter((item) => !item.permission || item.permission(permissions)),
	})).filter((section) => section.items.length > 0);

	const email = me?.email ?? '';
	const displayName = me?.name || (email ? displayNameFromEmail(email) : '—');
	const initials = email ? initialsFromEmail(email) : '·';

	const sidebarHealth: SidebarHealth = (() => {
		if (modulesQuery.isLoading) {
			return { status: 'loading', moduleCount: 0, checkedDeps: 0, issueCount: 0 };
		}
		if (modulesQuery.isError || !modulesQuery.data) {
			return { status: 'unknown', moduleCount: 0, checkedDeps: 0, issueCount: 0 };
		}
		const agg = aggregatePlatformHealth(modulesQuery.data);

		return {
			status: agg.status,
			moduleCount: agg.moduleCount,
			checkedDeps: agg.checkedDeps,
			issueCount: agg.issueCount,
		};
	})();

	const theme = HEALTH_THEME[sidebarHealth.status];
	const statusLabel = t(STATUS_LABEL_KEY[sidebarHealth.status]);
	const summary =
		sidebarHealth.issueCount > 0
			? t('STATUS_HEALTH_SUMMARY_ISSUES', {
					modules: sidebarHealth.moduleCount,
					issues: sidebarHealth.issueCount,
				})
			: t('STATUS_HEALTH_SUMMARY_OK', {
					modules: sidebarHealth.moduleCount,
					checked: sidebarHealth.checkedDeps,
				});

	return (
		<aside className="flex min-h-screen w-[240px] flex-col border-r border-subtle bg-grey-25 px-sm pb-lg pt-sm">
			<div className="mb-lg flex items-center gap-xs px-xs">
				<AnimatedBrand label={t('BRAND')} />
			</div>

			<button
				type="button"
				onClick={openPalette}
				className="mb-md flex items-center gap-2xs rounded-md border border-subtle bg-white px-xs py-3xs text-[12px] text-subtle transition-colors hover:border-default"
			>
				<Search className="h-sm w-sm" />
				<span className="flex-1 text-left">{t('SEARCH_THREADS')}</span>
				<kbd className="ml-auto font-mono text-[10px] leading-none text-subtle">⌘K</kbd>
			</button>

			{sections.map((section) => (
				<div key={section.labelKey} className="mb-md">
					<p className="mt-0 mb-3xs px-xs text-[10px] font-medium uppercase tracking-wider text-subtle">
						{t(section.labelKey)}
					</p>
					{section.filtersByModule && (
						<div className="mb-2xs px-3xs">
							<ModuleSelect modules={moduleNames} value={selectedModule} onChange={setSelectedModule} />
						</div>
					)}
					<nav className="flex flex-col gap-4xs">
						{section.items.map(({ to, labelKey, shortcut, icon: Icon }) => (
							<NavLink
								key={to}
								to={to}
								end={to === ROUTES.today}
								className={({ isActive }) => `${NAV_ITEM_BASE} ${isActive ? NAV_ITEM_ACTIVE : NAV_ITEM_IDLE}`}
							>
								<Icon className="h-[14px] w-[14px]" />
								<span className="flex-1">{t(labelKey)}</span>
								<kbd className="font-mono text-[10px] leading-none text-subtle">{shortcut}</kbd>
							</NavLink>
						))}
					</nav>
				</div>
			))}

			<p className="mt-md mb-3xs px-xs text-[10px] font-medium uppercase tracking-wider text-subtle">
				{t('SECTION_BOT_STATUS')}
			</p>
			<NavLink
				to={ROUTES.health}
				className={`mb-xs block rounded-md border px-xs py-xs ${theme.container}`}
				aria-label={statusLabel}
			>
				<div className="mb-3xs flex items-center gap-2xs">
					<span className="relative flex h-2xs w-2xs">
						<span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-50 ${theme.ping}`} />
						<span className={`relative inline-flex h-2xs w-2xs rounded-full ${theme.dot}`} />
					</span>
					<span className={`text-[11px] font-semibold leading-none ${theme.text}`}>{statusLabel}</span>
				</div>
				<p className={`text-[10px] leading-tight ${theme.text} opacity-80`}>{summary}</p>
			</NavLink>

			<div className="flex-1" />

			<div className="border-t border-subtle pt-sm">
				<div className="flex items-center gap-2xs rounded-md px-xs py-3xs">
					<div className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-purple-500 text-[10px] font-semibold leading-none text-white">
						{initials}
					</div>
					<div className="min-w-0 flex-1 leading-tight">
						<p className="truncate text-[12px] font-medium leading-none text-default">{displayName}</p>
						{email && <p className="mt-3xs truncate text-[11px] leading-none text-subtle">{email}</p>}
					</div>
				</div>
			</div>
		</aside>
	);
};
